import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Mail, Check } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();
      
      if (response.ok) {
        setSubmitted(true);
        toast.success('Reset link sent! Check your email.');
      } else {
        toast.error(data.detail || 'Something went wrong');
      }
    } catch (error) {
      toast.error('Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <img 
                src="/logo.jpg" 
                alt="Score Copilot" 
                className="h-14 w-auto object-contain"
              />
              <span className="font-heading text-2xl font-bold text-white uppercase">
                Score Copilot
              </span>
            </div>
          </div>

          <div className="bg-[#18181b] border border-white/10 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="text-emerald-500" size={32} />
            </div>
            
            <h1 className="font-heading text-2xl font-bold text-white uppercase mb-4">
              Check Your Email
            </h1>
            
            <p className="text-gray-400 mb-6">
              We've sent a password reset link to <span className="text-white">{email}</span>. 
              Click the link in the email to reset your password.
            </p>

            <p className="text-gray-500 text-sm mb-6">
              Didn't receive the email? Check your spam folder or try again.
            </p>

            <div className="space-y-3">
              <Button
                onClick={() => setSubmitted(false)}
                variant="outline"
                className="w-full border-white/10 text-white hover:bg-white/5"
              >
                Try Another Email
              </Button>
              
              <Link to="/login">
                <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white">
                  Back to Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img 
              src="/logo.jpg" 
              alt="Score Copilot" 
              className="h-14 w-auto object-contain"
            />
            <span className="font-heading text-2xl font-bold text-white uppercase">
              Score Copilot
            </span>
          </div>
          <h1 className="font-heading text-3xl font-bold text-white uppercase">
            Forgot Password?
          </h1>
          <p className="text-gray-400 mt-2">
            No worries, we'll send you reset instructions
          </p>
        </div>

        <div className="bg-[#18181b] border border-white/10 rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label className="text-gray-300">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="pl-10 bg-black/20 border-white/10 text-white placeholder:text-gray-500"
                  data-testid="forgot-email-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-6 btn-lift"
              data-testid="forgot-submit-btn"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="text-gray-400 hover:text-white text-sm flex items-center justify-center gap-2"
            >
              <ArrowLeft size={16} />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
